/**
 * formFieldClipboard — which field the form's right-click menu copies and pastes
 * (SYD-2232). A field registers under `${documentPath}#${fieldId}` and stamps that
 * key on its DOM box as `data-form-field-key`; `FormContextMenu` reads the key off
 * the right-clicked element and resolves the entry here, so one menu serves every
 * field. The key names the surface and `fieldId` names the field the envelope
 * carries — a meta field keys under `meta.<id>` and registers the bare id.
 *
 * `read` answers with the value ON SCREEN and `write` is `null` for a field that
 * refuses writes. Neither survives the clipboard `await` — the entry is resolved
 * again afterwards, because a field's gate can close while the read is in flight.
 * Registration is refcounted per key, mirroring `formUndoStack.registerTarget`.
 */

import type { ArCellValue } from '#types/System.Artifacts.types';
import type { FieldType } from '#types/System.types';

export type FieldClipboardTarget = {
    /** The value on screen, read when the menu item is clicked. */
    read: () => ArCellValue;
    /** The id the copied envelope carries and a table column matches on. */
    fieldId: string;
    fieldType: FieldType | undefined;
    /** `null` when the field refuses writes, which is what disables Paste Field. */
    write: ((value: ArCellValue) => void) | null;
};

/**
 * Registrations per key, oldest first. One key can be claimed by more than one
 * live surface — the same record rendered twice — so this is a stack, not a slot:
 * the last registration is the target in force and the key survives until the
 * last unregister.
 */
const targets = new Map<string, FieldClipboardTarget[]>();

/**
 * Refcounted registration: N mounts of one key survive until the Nth unregister,
 * so a surface unmounting cannot strip its twin's entry. The returned unregister
 * drops exactly its own registration and is idempotent, which is what makes
 * StrictMode's mount-cleanup-mount harmless.
 */
const registerTarget = (key: string, target: FieldClipboardTarget): (() => void) => {
    targets.set(key, [...(targets.get(key) ?? []), target]);
    let released = false;
    return () => {
        if (released) return;
        const registrations = targets.get(key);
        if (registrations === undefined) return;
        const index = registrations.lastIndexOf(target);
        if (index === -1) return;
        released = true;
        const remaining = [...registrations.slice(0, index), ...registrations.slice(index + 1)];
        if (remaining.length === 0) targets.delete(key);
        else targets.set(key, remaining);
    };
};

/** The most recently registered live target for a key, or `undefined` if none is mounted. */
const targetFor = (key: string): FieldClipboardTarget | undefined => targets.get(key)?.at(-1);

export const formFieldClipboard = { registerTarget, targetFor };
